import { useState } from "react";
import { Link, useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../lib/api";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [, setLocation] = useLocation();
  const qc = useQueryClient();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiFetch("/auth/login", {
        method: "POST",
        body: JSON.stringify({
          email: email.trim(),
          password,
        }),
      });
      await qc.invalidateQueries({ queryKey: ["me"] });
      setLocation("/overview");
    } catch (err: any) {
      setError(err?.message ?? "Could not sign in.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-slate-900">Thynra Receptionist</h1>
          <p className="mt-2 text-slate-600">
            Sign in to manage your AI receptionist.
          </p>
        </div>
        <form
          onSubmit={handleSubmit}
          className="bg-white shadow-sm rounded-xl p-6 border border-slate-200"
        >
          <label className="block">
            <span className="text-sm font-medium text-slate-700">
              Email address
            </span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@yourcompany.com"
              autoFocus
              autoComplete="username"
              className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:border-brand-600 focus:outline-none focus:ring-1 focus:ring-brand-600"
            />
          </label>
          <label className="block mt-4">
            <span className="text-sm font-medium text-slate-700">
              Password
            </span>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:border-brand-600 focus:outline-none focus:ring-1 focus:ring-brand-600"
            />
          </label>
          {error && (
            <p className="mt-3 text-sm text-red-600">{error}</p>
          )}
          <button
            type="submit"
            disabled={submitting || !email.trim() || !password}
            className="mt-5 w-full rounded-lg bg-brand-600 px-4 py-2.5 text-white font-medium hover:bg-brand-700 disabled:opacity-60 disabled:cursor-not-allowed transition"
          >
            {submitting ? "Signing in…" : "Sign in"}
          </button>
          <p className="mt-4 text-center text-sm">
            <Link
              href="/forgot-password"
              className="text-brand-700 hover:underline"
            >
              Forgot your password?
            </Link>
          </p>
        </form>
        <p className="mt-6 text-center text-sm text-slate-500">
          New here? Accounts are created by invitation. Ask your operator to
          send you one.
        </p>
      </div>
    </div>
  );
}
